import { ITheme, IStyle, mergeStyleSets, getTheme } from '@fluentui/react';
import React, { useMemo } from 'react';
import { IProductDetailPanelContentProps } from './content';
import { PriceDisplay } from '../price';
import { RatingBar } from '../../../components/rating-bar';
import { InfoLine } from '../info-line';
import { PRODUCT_DETAIL_VARIANT_SELECTOR_ID } from '.';
import { ProductVariantSelector } from '../pdct-variant-select';
import { BwjPalette } from '../../../../app-core/app-theme';
import { BwjButton, BwjButtonType } from '../../../../framework/components/bwj-button';
import { ProductVariantLabel } from '../pdct-variant-label';
import { isMeaningfulString } from '../../../../framework/utils/string';
import { RatingBreakdown } from '../rating-breakdown';
import { ProductImageBrowser } from '../pdct-image-browser';
import { PriceChangeNote } from './price-note';

const getStyleClasses = (theme: ITheme) => {
  const normalSpace = '0.75rem';
  const bigSpace = '1rem';
  const tinySpace = '0.1rem';
  const megaSpace = '2.5rem';

  const root: IStyle = {
    width: '100%',
    maxWidth: 500,
    overflow: 'hidden',
    margin: '0 auto',
    padding: 0,
  };

  const brand: IStyle = {
    fontSize: theme.fonts.xSmall.fontSize,
    lineHeight: theme.fonts.xSmall.lineHeight,
    fontWeight: 400,
    margin: 0,
    padding: 0,
  };

  const title: IStyle = {
    fontSize: theme.fonts.mediumPlus.fontSize,
    lineHeight: theme.fonts.mediumPlus.lineHeight,
    fontWeight: 600,
    margin: 0,
    padding: 0,
    marginBottom: normalSpace,
  };

  const keyStatsContainer: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    gap: bigSpace,
    alignItems: 'flex-start',
  };

  const ratingContainer: IStyle = {
    width: 1,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  };

  const mediaContainer: IStyle = {
    marginTop: bigSpace,
    marginBottom: bigSpace,
  }

  const variantSelector: IStyle = {
    marginBottom: bigSpace,
  };

  const subheading: IStyle = {
    fontSize: theme.fonts.medium.fontSize,
    lineHeight: theme.fonts.medium.lineHeight,
    fontWeight: 600,
    margin: 0,
    padding: 0,
    marginTop: bigSpace,
    marginBottom: normalSpace,
  };

  const actionContainer: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: megaSpace,
    marginBottom: megaSpace,
    gap: bigSpace,
  }

  const buttonContainer: IStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: tinySpace,
  };

  const section: IStyle = {
    margin: 0,
    padding: 0,

    '& > ul': {
      margin: 0,
    },

    '& p': {
      margin: 0,
      padding: 0,
      marginBottom: normalSpace,
    },
  }

  const reviewContainer: IStyle = {};

  return mergeStyleSets({
    root,
    brand,
    title,
    mediaContainer,
    keyStatsContainer,
    ratingContainer,
    variantSelector,
    subheading,
    actionContainer,
    buttonContainer,
    section,
    reviewContainer,
  });
}

export const ProductDetailPanelContentMobile: React.FunctionComponent<IProductDetailPanelContentProps> = (props) => {
  const theme = getTheme();
  const cs = getStyleClasses(theme);
  const { contentWidth } = props;
  const mediaWidth = useMemo(() => contentWidth <= 500 ? contentWidth : 500, [contentWidth]);

  return (
    <div className={cs.root}>
      <h3 className={cs.brand}>{props.brand}</h3>
      <h2 className={cs.title}>{props.title}</h2>
      {((!!props.rating && !!props.averageRating) || !!props.price) &&
      <div className={cs.keyStatsContainer}>
        {!!props.price &&
        <PriceDisplay
          price={props.price}
          fontSize={theme.fonts.superLarge}
        />}
        {!!props.rating && !!props.averageRating &&
        <div className={cs.ratingContainer}>
          <RatingBar
            rating={props.averageRating}
            reviewerCount={props.rating.totalReviewCount}
            rootStyle={{ width: '100%', maxWidth: 200 }}
          />
        </div>}
      </div>}
      {!!props.onIncludedServicesOverlayRequested && !!props.includedServices && props.includedServices.length > 0 &&
      <InfoLine
        icon={'LightBulb'}
        text={`Price includes ${props.includedServices.length} additional ${(props.includedServices.length > 1 ? 'services' : 'service')}`}
        onClick={() => !!props.onIncludedServicesOverlayRequested && props.onIncludedServicesOverlayRequested(props.includedServices!)}
      />}
      {props.priceChange !== undefined && props.priceChange !== 0 &&
      <div style={{ marginTop: '0.75rem' }}>
        <PriceChangeNote priceChange={props.priceChange} />
      </div>}
      <div className={cs.mediaContainer}>
        <ProductImageBrowser
          id={props.id}
          idPrefix='prd-dtl'
          images={props.imageUrls}
          imageWidth={mediaWidth}
          recommendation={props.recommendation}
        />
      </div>

      {props.variants.length > 1 &&
      <div id={PRODUCT_DETAIL_VARIANT_SELECTOR_ID} className={cs.variantSelector}>
        <h3 className={cs.variantSelector}>
          Available In (select one)
        </h3>
        <ProductVariantSelector
          swatchSize={5}
          options={props.variants}
          selected={props.selectedVariant}
          showLabels={true}
          onSelectionChanged={s => !!s && props.onVariantSelected(s)}
        />
      </div>}

      <div className={cs.actionContainer}>
        {isMeaningfulString(props.chooseProductButtonText) &&
        <div className={cs.buttonContainer}>
          {!props.selectedVariant &&
          <InfoLine
            icon={'Warning'}
            text={`Please select a  ${props.variantDescriptor}`}
            color={BwjPalette.themeWarning}
            rootStyle={{ marginBottom: '0.1rem', justifyContent: 'center', }}
          />}
          <BwjButton
            buttonStyle={BwjButtonType.PrimaryStandard}
            text={props.chooseProductButtonText}
            disabled={!props.selectedVariant}
            onClick={() => props.onProductChosen(props.selectedVariant!)}
          />
        </div>}
        {!!props.selectedVariant &&
        <ProductVariantLabel
          texture={props.selectedVariant}
          size={1.4}
        />}
      </div>

      <section className={cs.section}>
        <h3 className={cs.subheading}>Key Features</h3>
        <ul>
          {props.keyFeatures.map(feature =>
          <li key={feature}>{feature}</li>)}
        </ul>
      </section>
      {!!props.includedServices && props.includedServices.length > 1 &&
      <section className={cs.section}>
        <h3 className={cs.subheading}>Included At No Additional Cost</h3>
        <ul>
          {props.includedServices.map(incl =>
          <li key={incl}>{incl}</li>)}
        </ul>
      </section>}
      {!!props.rating && !!props.averageRating &&
      <section className={cs.section}>
        <h3 className={cs.subheading}>{`Average Customer Rating: ${props.averageRating.toFixed(1)}`}</h3>
        <div className={cs.reviewContainer}>
          {isMeaningfulString(props.rating.reviewSummary) &&
          <p>{props.rating.reviewSummary}</p>}
          <RatingBreakdown review={props.rating} />
        </div>
      </section>}
    </div>
  );
};